import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Upload, X, Save, Eye, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { focusAreaService } from './focusAreaService';

interface FocusAreaFormData {
  title: string;
  description: string;
  order: number;
  image?: File;
}

const FocusAreaForm: React.FC<{ isEdit?: boolean }> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploading] = useState(false);
  const [existingImage, setExistingImage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<FocusAreaFormData>({
    defaultValues: {
      title: '',
      description: '',
      order: 1,
    }
  });

  // Fetch existing data for edit
  useEffect(() => {
    if (isEdit && id) {
      fetchFocusArea();
    }
  }, [id, isEdit]);

  const fetchFocusArea = async () => {
    try {
      setLoading(true);
      const response = await focusAreaService.getById(parseInt(id!));
      
      if (response.success) {
        const data = response.data;
        reset({
          title: data.title,
          description: data.description,
          order: data.order,
        });
        
        if (data.image_url) {
          setImagePreview(data.image_url);
          setExistingImage(data.image_url);
        }
      } else {
        toast.error('Failed to fetch focus area');
        navigate('/admin/home/focus-areas');
      }
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch focus area');
      navigate('/admin/home/focus-areas');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid image (JPEG, PNG, WebP)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      setValue('image', file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const removeImage = () => {
    setValue('image', undefined);
    setImagePreview(existingImage || '');
  };

  const onSubmit = async (data: FocusAreaFormData) => {
    console.log('Form data submitted:', data);
    
    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('order', data.order.toString());
      
      console.log('FormData contents before append:');
      console.log('Title:', data.title);
      console.log('Description:', data.description);
      console.log('Order:', data.order);
      console.log('Has image file?', !!data.image);
      
      if (data.image) {
        formData.append('image', data.image);
        console.log('Image file:', data.image.name, data.image.size, data.image.type);
      } else if (isEdit && !existingImage) {
        formData.append('image', '');
      }
      
      // Log FormData entries
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }
      
      let response;
      if (isEdit && id) {
        console.log('Updating focus area ID:', id);
        response = await focusAreaService.update(parseInt(id), formData);
      } else {
        console.log('Creating new focus area');
        response = await focusAreaService.create(formData);
      }
      
      console.log('API Response:', response);
      
      if (response.success) {
        toast.success(`Focus area ${isEdit ? 'updated' : 'created'} successfully`);
        navigate('/admin/home/focus-areas');
      } else {
        console.error('API returned error:', response);
        toast.error(response.message || response.errors || 'Operation failed');
      }
    } catch (error: any) {
      console.error('Submit error details:', {
        error,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach(key => {
          toast.error(`${key}: ${errors[key].join(', ')}`);
        });
      } else {
        toast.error(error.response?.data?.message || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/home/focus-areas"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Focus Areas
        </Link>
        <h1 className="text-3xl font-bold text-gray-800">
          {isEdit ? 'Edit Focus Area' : 'Create New Focus Area'}
        </h1>
        <p className="text-gray-600 mt-2">
          {isEdit ? 'Update the focus area details' : 'Add a new focus area to display on the website'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Form Fields */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    {...register('title', { required: 'Title is required' })}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.title ? 'border-red-300' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Enter focus area title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    {...register('description', { required: 'Description is required' })}
                    rows={4}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.description ? 'border-red-300' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Enter focus area description"
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    {watch('description')?.length || 0}/500 characters
                  </p>
                </div>

                {/* Order only - Removed Status section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min="1"
                    {...register('order', { min: 1, valueAsNumber: true })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Lower numbers appear first
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Image Upload & Actions */}
          <div className="space-y-6">
            {/* Image Upload */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Focus Area Image</h2>
              
              {imagePreview ? (
                <div className="space-y-4">
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    {!existingImage && (
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => document.getElementById('image-upload')?.click()}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Change Image
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => document.getElementById('image-upload')?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <Upload className="text-gray-400" size={24} />
                  </div>
                  <p className="text-gray-600 mb-2">
                    <span className="text-blue-600 font-medium">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-sm text-gray-500">
                    PNG, JPG, WebP up to 5MB
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Recommended: 400×300px
                  </p>
                </div>
              )}
              
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              
              {uploading && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    <span className="text-blue-700">Uploading image...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Actions</h2>
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      <span>{isEdit ? 'Update Focus Area' : 'Create Focus Area'}</span>
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => navigate('/admin/home/focus-areas')}
                  className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                
                {isEdit && existingImage && (
                  <a
                    href={existingImage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Eye size={18} />
                    <span>View Image</span>
                  </a>
                )}
              </div>
            </div>

            {/* Information */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Tips</h3>
              <ul className="space-y-2 text-sm text-blue-700">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Use clear, high-quality images
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Keep descriptions concise but informative
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Set appropriate order for display sequence
                </li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default FocusAreaForm;